import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Property } from '@/types/property';
import { usePropertyMutations } from '@/hooks/usePropertyMutations';
import FormModal from '@/components/v2/molecules/admin/shared/FormModal';
import { Eye, EyeOff } from 'lucide-react';

interface PropertyWifiModalProps {
  isOpen: boolean;
  onClose: () => void;
  property: Property | null;
}

const formSchema = z.object({
  network: z.string().optional(),
  password: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

const PropertyWifiModal = ({ isOpen, onClose, property }: PropertyWifiModalProps) => {
  const { updateProperty } = usePropertyMutations();
  const [showPassword, setShowPassword] = useState(false);

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      network: '',
      password: '',
    },
  });

  useEffect(() => {
    if (isOpen && property) {
      form.reset({
        network: property.wifiNetwork || '',
        password: property.wifiPassword || '',
      });
      setShowPassword(false);
    }
  }, [property, isOpen, form]);

  const onSubmit = async (data: FormData) => {
    if (!property) return;

    try {
      await updateProperty.mutateAsync({
        id: property.id,
        wifiNetwork: data.network || null,
        wifiPassword: data.password || null,
      });
      onClose();
    } catch (error) {
      console.error('Error saving property wifi info:', error);
    }
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title="Configuración WiFi"
      description={property?.shortName || property?.title}
      form={form}
      onSubmit={onSubmit}
      submitLabel="Guardar"
      isLoading={updateProperty.isPending}
      maxWidth="max-w-[500px]"
    >
      <div className="space-y-4">
        <FormField
          control={form.control}
          name="network"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Red WiFi</FormLabel>
              <FormControl>
                <Input
                  placeholder="Nombre de la red WiFi"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Contraseña</FormLabel>
              <div className="relative">
                <FormControl>
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Contraseña WiFi"
                    className="pr-10"
                    {...field}
                  />
                </FormControl>
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </FormModal>
  );
};

export default PropertyWifiModal;
